import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateContractFormDTO,
  RenewContractFormDTO,
  UpdateContractFormDTO,
} from "@/modules/contracts/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { contractsApi } from "./client";

export const contractsKeys = {
  all: ["contracts"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["contracts", "list", q] as const,
  detail: (id: string) => ["contracts", "detail", id] as const,
  expiring: (q: Partial<OffsetPageQueryDTO & { days?: number; excludeDays?: number }>) =>
    ["contracts", "expiring", q] as const,
};

export function useContractsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: contractsKeys.list(q),
    queryFn: () => contractsApi.list(q),
  });
}

export function useContractQuery(id: string) {
  return useQuery({
    queryKey: contractsKeys.detail(id),
    queryFn: () => contractsApi.get(id),
    enabled: !!id,
  });
}

export function useExpiringContractsQuery(
  query: Partial<OffsetPageQueryDTO & { days?: number; excludeDays?: number }> = {},
  options?: { enabled?: boolean },
) {
  const q: OffsetPageQueryDTO & { days?: number; excludeDays?: number } = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    days: query.days,
    excludeDays: query.excludeDays,
  };
  return useQuery({
    queryKey: contractsKeys.expiring(q),
    queryFn: () => contractsApi.getExpiring(q),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractFormDTO & { documents?: Array<{ id: string; title: string; fileKey: string | null; documentType: "main" | "attachment"; notes?: string | null }> }) =>
      contractsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງສັນຍາສໍາເລັດ");
      qc.invalidateQueries({ queryKey: contractsKeys.all });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "ສ້າງສັນຍາລົ້ມເຫຼວ"),
  });
}

export function useUpdateContract(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContractFormDTO) =>
      contractsApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂສັນຍາສໍາເລັດ");
      qc.invalidateQueries({ queryKey: contractsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: contractsKeys.all });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "ແກ້ໄຂສັນຍາລົ້ມເຫຼວ"),
  });
}

export function useRenewContract(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RenewContractFormDTO) =>
      contractsApi.renew(id, input),
    onSuccess: () => {
      toast.success("ຕໍ່ອາຍຸສັນຍາສໍາເລັດ");
      qc.invalidateQueries({ queryKey: contractsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: contractsKeys.all });
      qc.invalidateQueries({ queryKey: contractsKeys.expiring({}) });
    },
    onError: () => toast.error("ຕໍ່ອາຍຸສັນຍາລົ້ມເຫຼວ"),
  });
}

export function useDeleteContract() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => contractsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
